/**
 * Paginated Benepass transfer rows with filters (eligible participant ID, status, dates, partner product).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge } from '@/components/common/Badge';
import { Card, CardBody, CardHeader } from '@/components/common/Card';
import type { Column } from '@/components/common/Table';
import { Pagination, Table } from '@/components/common/Table';
import type {
  BenepassTransferListItem,
  BenepassTransferListResponse,
  TransferListParams,
} from '@/app/services/benepassTransferService';
import { fetchTransferList } from '@/app/services/benepassTransferService';
import { transferStatusBadgeVariant } from '@/pages/BenepassTransfers/transferStatusBadgeVariant';

import styles from './TransferList.module.scss';

const PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  '',
  'PENDING',
  'SKIPPED_NO_CARD',
  'SKIPPED_NO_PURSE',
  'SENT',
  'SUCCESS',
  'FAILED',
] as const;

type FiltersState = {
  eligibleParticipantId: string;
  status: string;
  dateFrom: string;
  dateTo: string;
  partnerProductId: string;
};

const emptyFilters = (): FiltersState => ({
  eligibleParticipantId: '',
  status: '',
  dateFrom: '',
  dateTo: '',
  partnerProductId: '',
});

function parsePositiveInt(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  const n = Number.parseInt(trimmed, 10);
  return Number.isFinite(n) ? n : undefined;
}

function buildListParams(page: number, applied: FiltersState): TransferListParams {
  const eligible_participant_id = parsePositiveInt(applied.eligibleParticipantId);
  const partner_product_id = parsePositiveInt(applied.partnerProductId);
  const params: TransferListParams = {
    page,
    page_size: PAGE_SIZE,
  };
  if (eligible_participant_id !== undefined) {
    params.eligible_participant_id = eligible_participant_id;
  }
  if (applied.status.trim() !== '') {
    params.status = applied.status.trim();
  }
  if (applied.dateFrom.trim() !== '') {
    params.date_from = applied.dateFrom.trim();
  }
  if (applied.dateTo.trim() !== '') {
    params.date_to = applied.dateTo.trim();
  }
  if (partner_product_id !== undefined) {
    params.partner_product_id = partner_product_id;
  }
  return params;
}

function formatMoney(amount: string): string {
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(isoOrDate: string): string {
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return isoOrDate;
  return d.toLocaleString();
}

export const BenepassTransferList: React.FC = () => {
  const [appliedFilters, setAppliedFilters] = useState<FiltersState>(emptyFilters());
  const [draftFilters, setDraftFilters] = useState<FiltersState>(emptyFilters());
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<BenepassTransferListResponse | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildListParams(page, appliedFilters);
      const response = await fetchTransferList(params);
      setData(response);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, appliedFilters]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = useMemo(() => {
    if (data == null || data.total <= 0) return 0;
    return Math.ceil(data.total / data.page_size);
  }, [data]);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({ ...draftFilters });
    setPage(1);
  }, [draftFilters]);

  const handleResetFilters = useCallback(() => {
    const cleared = emptyFilters();
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
    setPage(1);
  }, []);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleApplyFilters();
      }
    },
    [handleApplyFilters]
  );

  const columns = useMemo((): Column<BenepassTransferListItem>[] => {
    return [
      {
        key: 'id',
        header: 'ID',
        width: '6rem',
      },
      {
        key: 'reference_number',
        header: 'Reference',
        render: (row) => <span>{row.reference_number}</span>,
      },
      {
        key: 'eligible_participant_id',
        header: 'Participant',
        width: '8rem',
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => (
          <Badge variant={transferStatusBadgeVariant(row.status)} size="sm">
            {row.status}
          </Badge>
        ),
      },
      {
        key: 'reward_amount',
        header: 'Amount',
        align: 'right',
        render: (row) => formatMoney(row.reward_amount),
      },
      {
        key: 'partner_name',
        header: 'Partner',
        render: (row) => (
          <>
            {row.partner_name ?? row.doing_business_as ?? '—'}
            {row.partner_product_id != null && (
              <span className={styles.mutedSuffix}> (#{row.partner_product_id})</span>
            )}
          </>
        ),
      },
      {
        key: 'created_at',
        header: 'Created',
        render: (row) => formatDate(row.created_at),
      },
    ];
  }, []);

  const showEmpty =
    !loading && error == null && data != null && data.items.length === 0;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>All Benepass transfers</h1>
          <p className={styles.pageDescription}>
            Filter by participant, status, partner product, or creation date range.
          </p>
        </div>
        <a className={styles.toolbarLink} href="#/benepass-transfers">
          Back to summary
        </a>
      </header>

      <div className="filters-section" onKeyPress={handleKeyPress}>
        <div className="filters-content">
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="benepass-filter-participant">Eligible participant ID</label>
              <input
                id="benepass-filter-participant"
                type="text"
                inputMode="numeric"
                placeholder="Participant ID…"
                value={draftFilters.eligibleParticipantId}
                onChange={(ev) =>
                  setDraftFilters((f) => ({ ...f, eligibleParticipantId: ev.target.value }))
                }
              />
            </div>
            <div className="filter-group">
              <label htmlFor="benepass-filter-status">Status</label>
              <select
                id="benepass-filter-status"
                value={draftFilters.status}
                onChange={(ev) => setDraftFilters((f) => ({ ...f, status: ev.target.value }))}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt === '' ? '_any' : opt} value={opt}>
                    {opt === '' ? 'Any' : opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="benepass-filter-partner-product">Partner product ID</label>
              <input
                id="benepass-filter-partner-product"
                type="text"
                inputMode="numeric"
                placeholder="Product ID…"
                value={draftFilters.partnerProductId}
                onChange={(ev) =>
                  setDraftFilters((f) => ({ ...f, partnerProductId: ev.target.value }))
                }
              />
            </div>
            <div className="filter-group">
              <label htmlFor="benepass-filter-from">Date from</label>
              <input
                id="benepass-filter-from"
                type="date"
                value={draftFilters.dateFrom}
                onChange={(ev) => setDraftFilters((f) => ({ ...f, dateFrom: ev.target.value }))}
              />
            </div>
            <div className="filter-group">
              <label htmlFor="benepass-filter-to">Date to</label>
              <input
                id="benepass-filter-to"
                type="date"
                value={draftFilters.dateTo}
                onChange={(ev) => setDraftFilters((f) => ({ ...f, dateTo: ev.target.value }))}
              />
            </div>
            <div className="filter-actions">
              <button type="button" className="btn-primary" onClick={handleApplyFilters}>
                Apply filters
              </button>
              <button type="button" className="btn-secondary" onClick={handleResetFilters}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className={styles.loading}>Loading transfers…</div>
      )}

      {error != null && (
        <div className={styles.errorMessage} role="alert">
          Failed to load transfers: {error}
        </div>
      )}

      {!loading && error == null && data != null && (
        <div className={styles.resultsBlock}>
          <Card>
            <CardHeader title="Transfer rows" subtitle={`${data.total} total matching filters`} />
            <CardBody>
              {showEmpty ? (
                <p className={styles.pageDescription}>No transfers found.</p>
              ) : (
                <>
                  <Table
                    columns={columns}
                    data={data.items}
                    keyExtractor={(row) => String(row.id)}
                    emptyMessage="No transfers found."
                  />
                  <Pagination
                    currentPage={data.page}
                    totalPages={totalPages}
                    totalItems={data.total}
                    itemsPerPage={data.page_size}
                    onPageChange={(p) => setPage(p)}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BenepassTransferList;
